import React, { useMemo, memo } from 'react';
import { format } from 'date-fns';
import { ReportResult } from '../../../models';
import { DataTable, ReportLabels } from '../../../components/data-table';
import { TableColumn } from '@leasequery/lq.react-component-library/dist/models';
import { DATE_TIME_FORMAT } from '../../../constants';
import {
  TableCell,
  TableConfig,
} from '@leasequery/lq.react-component-library/dist/components/table';

import ReportNameCell from './ReportNameCell';
import createArchiveButtonCell from './createArchiveButtonCell';
import { RecentReportCellClickHandler } from '.';
import { escapeRegExp, noop } from '../../../util';
import { useAuth } from '../../../context';
import createStatusCell from './createStatusCell';
import createExportToExcelButtonCell from './createExportToExcelButtonCell';
import { useIsFrs102Enabled } from '../../../util/hooks/useIsFrs102Enabled';
import { replaceIfrsLabels } from '../../../util/frs102Helper';

interface RecentReportsTableProps {
  reportResults: ReportResult[];
  onArchiveClick?: RecentReportCellClickHandler;
}

const tableConfig: TableConfig = {
  globalFilterDetector: (item: ReportResult, value: string) =>
    new RegExp(escapeRegExp(value), 'i').test(item.taskName),
};

const createTableColumns = (
  onArchiveClick: RecentReportCellClickHandler,
  isMultiClientUser?: boolean,
): TableColumn[] => {
  const ArchiveButtonCell = createArchiveButtonCell(onArchiveClick);
  const StatusCell = createStatusCell();
  const ExportToExcelButtonCell = createExportToExcelButtonCell();
  const columns: TableColumn[] = [
    {
      Header: 'Report Name',
      Cell: ReportNameCell,
      accessor: 'taskName',
    },
    {
      Header: 'Status',
      Cell: StatusCell,
      accessor: 'statusId',
      width: '8rem',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
    {
      Header: 'Run Date',
      Cell: (cell: TableCell): string => {
        return cell.value
          ? format(cell.value, DATE_TIME_FORMAT.FULL_DATE_TIME_WITH_MONTH_NAME)
          : null;
      },
      accessor: 'requestedTime',
      width: '19rem',
    },
    {
      Header: 'Export To Excel',
      Cell: ExportToExcelButtonCell,
      accessor: 'ExportToExcel',
      width: '16rem',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
    {
      Header: 'Archive',
      Cell: ArchiveButtonCell,
      accessor: 'taskId',
      width: '8rem',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
  ];

  if (isMultiClientUser) {
    columns.splice(columns.length - 1, 0, {
      Header: 'Client',
      accessor: 'companyName',
    });
  }

  return columns;
};

const labels: ReportLabels = {
  itemCount: 'Report Count:',
  searchPlaceHolder: 'Search Report Results',
};

function RecentReportsTable({
  reportResults,
  onArchiveClick,
}: RecentReportsTableProps): JSX.Element {
  const { user } = useAuth();
  const columns = useMemo(
    () => createTableColumns(onArchiveClick, user.isMultiClientUser),
    [onArchiveClick, user],
  );

  const isFrs102Enabled = useIsFrs102Enabled();
  const updatedReportResults: ReportResult[] = reportResults.map(result => ({
    ...result,
    taskName: replaceIfrsLabels(result.taskName, isFrs102Enabled),
  }));

  return (
    <DataTable
      columns={columns}
      data={updatedReportResults}
      config={tableConfig}
      labels={labels}
    />
  );
}

RecentReportsTable.defaultProps = {
  onArchiveClick: noop,
};

export default memo(RecentReportsTable);
